import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Grid, Divider, Button, CircularProgress, TextField } from '@mui/material';
import { ArrowBack, Receipt, AccountBalanceWallet, CheckCircle, PendingActions, AccountBalance, MoneyOff, TrendingDown } from '@mui/icons-material';
import { clientService } from '../../../shared/services/clientService';
import { invoiceService } from '../../../shared/services/invoiceService';
import { Client } from '../../../shared/types/client';
import { AppTable } from '../../../shared/components/AppTable';
import { AppButton } from '../../../shared/components/AppButton';
import { AppTablePagination } from '../../../shared/components/AppTablePagination';
import { AppSearch } from '../../../shared/components/AppSearch';
import { TableRow, TableCell } from '@mui/material';

const formatDate = (dateValue: any) => {
    if (!dateValue) return '-';
    try {
        const d = new Date(dateValue);
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
        return '-';
    }
};

export const ViewClient: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [client, setClient] = useState<Client | null>(null);
    const [statistics, setStatistics] = useState<any>(null);
    const [allInvoices, setAllInvoices] = useState<any[]>([]);
    const [creditedPayments, setCreditedPayments] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'invoices' | 'payments'>('invoices');

    const [pendingDates, setPendingDates] = useState({ from: '', to: '' });
    const [appliedDates, setAppliedDates] = useState({ from: '', to: '' });

    const [searchInput, setSearchInput] = useState('');
    const [invoicesPage, setInvoicesPage] = useState(0);
    const [invoicesPageSize, setInvoicesPageSize] = useState(10);
    const [paymentsPage, setPaymentsPage] = useState(0);
    const [paymentsPageSize, setPaymentsPageSize] = useState(10);

    useEffect(() => {
        const fetchClientData = async () => {
            if (!id) return;
            setIsLoading(true);
            try {
                const clientRes = await clientService.getById(id);
                // Handle various response structures
                let clientData: any = clientRes;
                if (clientRes?.data) {
                    if (clientRes.data.client) clientData = clientRes.data.client;
                    else if (clientRes.data.result) clientData = clientRes.data.result;
                    else clientData = clientRes.data;
                }
                setClient(clientData as Client);

                const statsRes = await invoiceService.getClientStatistics(id);
                setStatistics(statsRes.data);

                const invoicesRes = await invoiceService.getAllInvoicesWithAllStatus(id);
                const invData = invoicesRes?.data?.data || invoicesRes?.data?.items || invoicesRes?.data || invoicesRes?.items || [];
                setAllInvoices(Array.isArray(invData) ? invData : []);

                const creditedRes = await invoiceService.getCreditedPayments(id);
                const credData = creditedRes?.data?.data || creditedRes?.data?.items || creditedRes?.data || creditedRes?.items || [];
                setCreditedPayments(Array.isArray(credData) ? credData : []);
            } catch (error) {
                console.error("Error fetching client details", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchClientData();
    }, [id]);

    const filteredInvoices = React.useMemo(() => {
        let list = allInvoices;
        if (searchInput.trim()) {
            const q = searchInput.trim().toLowerCase();
            list = list.filter(inv => String(inv.invoiceNumber || '').toLowerCase().includes(q));
        }
        if (appliedDates.from) {
            const f = new Date(appliedDates.from).getTime();
            list = list.filter(inv => new Date(inv.invoiceDate).getTime() >= f);
        }
        if (appliedDates.to) {
            const t = new Date(appliedDates.to).setHours(23, 59, 59, 999);
            list = list.filter(inv => new Date(inv.invoiceDate).getTime() <= t);
        }
        return list;
    }, [allInvoices, appliedDates, searchInput]);

    const paginatedInvoices = React.useMemo(() => {
        const start = invoicesPage * invoicesPageSize;
        return filteredInvoices.slice(start, start + invoicesPageSize);
    }, [filteredInvoices, invoicesPage, invoicesPageSize]);

    const filteredPayments = React.useMemo(() => {
        let list = creditedPayments;
        if (searchInput.trim()) {
            const q = searchInput.trim().toLowerCase();
            list = list.filter(p => {
                const invNo = String(p.invoiceNumber || p.invoiceId?.invoiceNumber || '').toLowerCase();
                return invNo.includes(q);
            });
        }
        if (appliedDates.from) {
            const f = new Date(appliedDates.from).getTime();
            list = list.filter(p => new Date(p.date).getTime() >= f);
        }
        if (appliedDates.to) {
            const t = new Date(appliedDates.to).setHours(23, 59, 59, 999);
            list = list.filter(p => new Date(p.date).getTime() <= t);
        }
        return list;
    }, [creditedPayments, appliedDates, searchInput]);

    const paginatedPayments = React.useMemo(() => {
        const start = paymentsPage * paymentsPageSize;
        return filteredPayments.slice(start, start + paymentsPageSize);
    }, [filteredPayments, paymentsPage, paymentsPageSize]);

    const handleApplyFilters = () => {
        setAppliedDates(pendingDates);
        setInvoicesPage(0);
        setPaymentsPage(0);
    };
    const handleResetFilters = () => {
        setPendingDates({ from: '', to: '' });
        setAppliedDates({ from: '', to: '' });
        setSearchInput('');
        setInvoicesPage(0);
        setPaymentsPage(0);
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (!client) {
        return <Typography variant="h6" align="center" mt={4}>Client not found</Typography>;
    }

    return (
        <React.Fragment>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b border-gray-200 bg-white">
                <div className="flex items-center gap-4">
                    <Button
                        startIcon={<ArrowBack />}
                        onClick={() => navigate('/clients')}
                        sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'gray.100' } }}
                    >
                        Back
                    </Button>
                    <Typography variant="h4" fontWeight="bold">
                        {client.displayName || client.companyName || client.ownerName}
                    </Typography>
                </div>
            </div>

            <Box p={4} sx={{ backgroundColor: '#f9fafb', minHeight: '100vh' }}>
                <Grid container spacing={4}>
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 4, borderRadius: 2 }}>
                            <Typography variant="h6" fontWeight="bold" mb={2}>Client Details</Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Box display="flex" flexDirection="column" gap={1.5}>
                                <Typography><strong>Owner:</strong> {client.ownerName}</Typography>
                                <Typography><strong>Phone:</strong> {client.phone}</Typography>
                                <Typography><strong>Email:</strong> {client.email}</Typography>
                                <Typography><strong>Address:</strong> {[client.address, client.city, client.state, client.country].filter(Boolean).join(', ')}</Typography>
                                {client.gst && <Typography><strong>GSTIN:</strong> {client.gst}</Typography>}
                                {(client as any).panNumber && <Typography><strong>PAN:</strong> {(client as any).panNumber}</Typography>}
                            </Box>
                        </Paper>
                    </Grid>

                    {statistics && (
                        <Grid item xs={12} md={8}>
                            <Paper sx={{ p: 4, borderRadius: 2, height: '100%' }}>
                                <Typography variant="h6" fontWeight="bold" mb={2}>Statistics</Typography>
                                <Divider sx={{ mb: 2 }} />
                                <Grid container spacing={3}>
                                    {[
                                        { title: 'Total Invoices', value: statistics.totalInvoiceCount || 0, bg: 'bg-blue-50', border: 'border-blue-200', textTheme: 'text-blue-700', valueTheme: 'text-blue-900', icon: <Receipt fontSize="small" /> },
                                        { title: 'Invoice Amount', value: `₹${(statistics.totalInvoiceAmount || 0).toFixed(2)}`, bg: 'bg-indigo-50', border: 'border-indigo-200', textTheme: 'text-indigo-700', valueTheme: 'text-indigo-900', icon: <AccountBalanceWallet fontSize="small" /> },
                                        { title: 'Credited Amount', value: `₹${(statistics.totalCreditedAmount || 0).toFixed(2)}`, bg: 'bg-green-50', border: 'border-green-300', textTheme: 'text-green-700', valueTheme: 'text-green-900', icon: <CheckCircle fontSize="small" /> },
                                        { title: 'Pending Amount', value: `₹${(statistics.totalPendingAmount || 0).toFixed(2)}`, bg: 'bg-amber-50', border: 'border-amber-300', textTheme: 'text-amber-700', valueTheme: 'text-amber-900', icon: <PendingActions fontSize="small" /> },
                                        { title: 'Tax Amount', value: `₹${(statistics.totalTaxAmount || 0).toFixed(2)}`, bg: 'bg-purple-50', border: 'border-purple-200', textTheme: 'text-purple-700', valueTheme: 'text-purple-900', icon: <AccountBalance fontSize="small" /> },
                                        { title: 'TDS Amount', value: `₹${(statistics.totalTdsAmount || 0).toFixed(2)}`, bg: 'bg-rose-50', border: 'border-rose-200', textTheme: 'text-rose-700', valueTheme: 'text-rose-900', icon: <MoneyOff fontSize="small" /> },
                                        { title: 'Loss Amount', value: `₹${(statistics.totalLossAmount || 0).toFixed(2)}`, bg: 'bg-slate-100', border: 'border-slate-300', textTheme: 'text-slate-700', valueTheme: 'text-slate-900', icon: <TrendingDown fontSize="small" /> },
                                    ].map((stat, idx) => (
                                        <Grid item xs={12} sm={6} md={4} key={idx}>
                                            <div className={`p-4 rounded-xl border ${stat.bg} ${stat.border} shadow-sm flex flex-col justify-center h-full transition-all duration-200 hover:shadow-md hover:-translate-y-1`}>
                                                <div className="flex items-center gap-2 mb-2">
                                                    <span className={`flex justify-center items-center w-8 h-8 rounded-full bg-white bg-opacity-60 ${stat.textTheme}`}>
                                                        {stat.icon}
                                                    </span>
                                                    <Typography variant="body2" className={`${stat.textTheme} font-semibold`}>
                                                        {stat.title}
                                                    </Typography>
                                                </div>
                                                <Typography variant="h5" className={`${stat.valueTheme} font-bold mt-1 tracking-tight`}>
                                                    {stat.value}
                                                </Typography>
                                            </div>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Paper>
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <Paper sx={{ p: 0, borderRadius: 2, overflow: 'hidden' }}>
                            <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50', display: 'flex' }}>
                                <Button
                                    onClick={() => setActiveTab('invoices')}
                                    sx={{
                                        py: 2, px: 4, borderRadius: 0,
                                        borderBottom: 2,
                                        borderColor: activeTab === 'invoices' ? 'primary.main' : 'transparent',
                                        color: activeTab === 'invoices' ? 'primary.main' : 'text.secondary',
                                        fontWeight: activeTab === 'invoices' ? 'bold' : 'normal'
                                    }}
                                >
                                    All Invoices
                                </Button>
                                <Button
                                    onClick={() => setActiveTab('payments')}
                                    sx={{
                                        py: 2, px: 4, borderRadius: 0,
                                        borderBottom: 2,
                                        borderColor: activeTab === 'payments' ? 'primary.main' : 'transparent',
                                        color: activeTab === 'payments' ? 'primary.main' : 'text.secondary',
                                        fontWeight: activeTab === 'payments' ? 'bold' : 'normal'
                                    }}
                                >
                                    Credited Payments
                                </Button>
                            </Box>

                            <Box p={3}>
                                {/* Filters Section */}
                                <div className="mb-6 flex flex-wrap gap-4 items-end bg-gray-50 p-4 rounded-lg">
                                    <AppSearch
                                        value={searchInput}
                                        onChange={(v) => { setSearchInput(v); setInvoicesPage(0); setPaymentsPage(0); }}
                                        onSearch={() => { setInvoicesPage(0); setPaymentsPage(0); }}
                                        placeholder="Search Invoice No."
                                        className="w-full sm:w-auto min-w-[200px]"
                                    />
                                    <TextField
                                        label="Date From"
                                        type="date"
                                        size="small"
                                        value={pendingDates.from}
                                        onChange={e => setPendingDates(p => ({ ...p, from: e.target.value }))}
                                        InputLabelProps={{ shrink: true }}
                                        className="w-full sm:w-auto min-w-[160px]"
                                    />
                                    <TextField
                                        label="Date To"
                                        type="date"
                                        size="small"
                                        value={pendingDates.to}
                                        onChange={e => setPendingDates(p => ({ ...p, to: e.target.value }))}
                                        InputLabelProps={{ shrink: true }}
                                        className="w-full sm:w-auto min-w-[160px]"
                                    />
                                    <div className="flex gap-2">
                                        <AppButton variant="primary" onClick={handleApplyFilters} sx={{ px: 2, height: '40px' }}>Filter</AppButton>
                                        <AppButton variant="secondary" onClick={handleResetFilters} sx={{ px: 2, height: '40px' }}>Reset</AppButton>
                                    </div>
                                </div>

                                {activeTab === 'invoices' && (
                                    <>
                                        <AppTable
                                            head={
                                                <TableRow className="bg-gray-100">
                                                    <TableCell className="!font-semibold">No.</TableCell>
                                                    <TableCell className="!font-semibold">Date</TableCell>
                                                    <TableCell className="!font-semibold">Invoice Number</TableCell>
                                                    <TableCell className="!font-semibold">Status</TableCell>
                                                    <TableCell className="!font-semibold">Grand Total</TableCell>
                                                    <TableCell className="!font-semibold">Paid</TableCell>
                                                    <TableCell className="!font-semibold">Pending</TableCell>
                                                </TableRow>
                                            }
                                            isEmpty={!filteredInvoices || filteredInvoices.length === 0}
                                            emptyColSpan={7}
                                        >
                                            {Array.isArray(paginatedInvoices) && paginatedInvoices.map((inv, index) => (
                                                <TableRow key={inv._id} hover className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                    <TableCell>{invoicesPage * invoicesPageSize + index + 1}</TableCell>
                                                    <TableCell>{formatDate(inv.invoiceDate)}</TableCell>
                                                    <TableCell className="!font-medium">{inv.invoiceNumber}</TableCell>
                                                    <TableCell>
                                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${inv.status === 'Paid' ? 'bg-green-100 text-green-800' :
                                                            inv.status === 'Partial' ? 'bg-yellow-100 text-yellow-800' :
                                                                'bg-red-100 text-red-800'
                                                            }`}>
                                                            {inv.status}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell>₹{(inv.grandTotalAmount || inv.totalAmount || inv.grandTotal || 0).toFixed(2)}</TableCell>
                                                    <TableCell>₹{((inv.advancePaidAmount || 0) + (inv.partialPaidAmount || 0)).toFixed(2)}</TableCell>
                                                    <TableCell>₹{inv.totalPendingAmount?.toFixed(2)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </AppTable>
                                        <AppTablePagination
                                            totalRows={filteredInvoices.length}
                                            page={invoicesPage}
                                            pageSize={invoicesPageSize}
                                            onPageChange={(_, p) => setInvoicesPage(p - 1)}
                                            onPageSizeChange={(size) => { setInvoicesPageSize(size); setInvoicesPage(0); }}
                                        />
                                    </>
                                )}

                                {activeTab === 'payments' && (
                                    <>
                                        <AppTable
                                            head={
                                                <TableRow className="bg-gray-100">
                                                    <TableCell className="!font-semibold">No.</TableCell>
                                                    <TableCell className="!font-semibold">Date</TableCell>
                                                    <TableCell className="!font-semibold">Amount</TableCell>
                                                    <TableCell className="!font-semibold">Account</TableCell>
                                                    <TableCell className="!font-semibold">Invoice Number</TableCell>
                                                </TableRow>
                                            }
                                            isEmpty={!filteredPayments || filteredPayments.length === 0}
                                            emptyColSpan={5}
                                        >
                                            {Array.isArray(paginatedPayments) && paginatedPayments.map((payment, index) => (
                                                <TableRow key={payment._id} hover className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                    <TableCell>{paymentsPage * paymentsPageSize + index + 1}</TableCell>
                                                    <TableCell>{formatDate(payment.date)}</TableCell>
                                                    <TableCell className="!font-medium !text-green-700">₹{payment.amount?.toFixed(2)}</TableCell>
                                                    <TableCell>{payment.accountName || '-'}</TableCell>
                                                    <TableCell>{payment.invoiceNumber || payment.invoiceId?.invoiceNumber || '-'}</TableCell>
                                                </TableRow>
                                            ))}
                                        </AppTable>
                                        <AppTablePagination
                                            totalRows={filteredPayments.length}
                                            page={paymentsPage}
                                            pageSize={paymentsPageSize}
                                            onPageChange={(_, p) => setPaymentsPage(p - 1)}
                                            onPageSizeChange={(size) => { setPaymentsPageSize(size); setPaymentsPage(0); }}
                                        />
                                    </>
                                )}
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
        </React.Fragment>
    );
};
